import { findEntry } from '@mypoz/i18n';

import { ACCOUNT_PROFILE_ROUTE, CHINA_PAYMENT_RECORDS_ROUTE } from '@/shell/navigation.js';

/**
 * The notification categories, in the order the filter dropdown lists them.
 *
 * Rebuilt for MYP-112 against a live full-account fetch (103 rows): `get_msgs` returns exactly
 * six type codes — `PACKAGE`, `ORDER`, `AB`, `ALIPAY`, `AUTH`, `COMPEN` — nothing else appeared,
 * and nothing here is invented against what the endpoint might one day send. The old ten-entry
 * list (MYP-66) mixed two confirmed codes with eight carried over from the old system's own
 * 站内信息 type filter as a guess; four of those (`FORWARDING`, `REGISTER`, `TOPUP`, `PAYMENT`)
 * don't correspond to anything this endpoint actually sends and are dropped rather than kept as
 * dead filter options nobody's data will ever match.
 *
 * The four newly-confirmed non-PACKAGE/ORDER codes reuse the old system's own labels rather than
 * new ones, because the vendor's own `type_cn` for each matches one of those labels exactly:
 * AB's `代购` is `dashboard.notifications.category.proxyBuy`'s own Chinese copy, AUTH's `认证`
 * is `verification`'s, and COMPEN's `申请赔偿` is `claim`'s, word for word — confirmed live, not
 * guessed twice. `ALIPAY` was already confirmed in MYP-66 and is unchanged.
 *
 * ALIPAY (MYP-69) opens the China Payment request history, `CHINA_PAYMENT_RECORDS_ROUTE`. Its row
 * shows a fixed summary instead of the reference: ALIPAY's `tid` is an internal message id, a
 * number the customer has never seen. The history page itself is waiting on `getalipaylist`
 * (api-issues #26); the notification already points at the page that will hold it, so nothing
 * here changes when that is fixed.
 *
 * Only PACKAGE and ORDER resolve a real per-notification link (`resolveNotificationLink`); every
 * other code here still just names a fixed landing page. Labels stay short on purpose — they
 * render inside a fixed-width badge.
 */
export const NOTIFICATION_TYPES = [
  { code: 'PACKAGE', labelKey: 'dashboard.notifications.category.package', destination: null },
  { code: 'ORDER', labelKey: 'dashboard.notifications.category.order', destination: null },
  {
    code: 'AB',
    labelKey: 'dashboard.notifications.category.proxyBuy',
    destination: '/china-payment/proxy-payment',
  },
  {
    code: 'ALIPAY',
    labelKey: 'dashboard.notifications.category.alipay',
    summaryKey: 'notifications.alipaySummary',
    destination: CHINA_PAYMENT_RECORDS_ROUTE,
  },
  {
    code: 'AUTH',
    labelKey: 'dashboard.notifications.category.verification',
    // The profile's verification row (MYP-142). There is no security page: removed under MYP-85.
    destination: ACCOUNT_PROFILE_ROUTE,
  },
  {
    code: 'COMPEN',
    labelKey: 'dashboard.notifications.category.claim',
    destination: '/wallet/additional-payment',
  },
] as const;

/**
 * The i18n key for a type's badge label, or undefined for a `type` the frontend doesn't know —
 * the endpoint could still add a seventh code tomorrow, so callers fall back to showing the raw
 * value rather than dropping the notification.
 */
export function getCategoryLabelKey(type: string) {
  return findEntry(NOTIFICATION_TYPES, type)?.labelKey;
}

/**
 * The i18n key for a fixed line a row shows in place of its reference, for a type whose reference
 * means nothing to the customer (ALIPAY's internal message id, MYP-69). Undefined for every other
 * type, which keeps showing its order, tracking or account number.
 */
export function getSummaryKey(type: string) {
  const entry = NOTIFICATION_TYPES.find((item) => item.code === type);
  return entry && 'summaryKey' in entry ? entry.summaryKey : undefined;
}

/**
 * Where selecting a notification of *this category* should take the user, or null when the
 * category has no fixed landing page.
 *
 * These are per-category landing pages, not per-notification links — a category-wide fallback for
 * every type except PACKAGE and ORDER, both of which resolve a real per-notification link instead
 * (`resolveNotificationLink`, which calls this function for every other category and only falls
 * back to `null` — open in place — for a PACKAGE notification whose subject can't be found, or an
 * ORDER notification with no `orderno` to link from).
 */
export function getNotificationDestination(type: string): string | null {
  return NOTIFICATION_TYPES.find((entry) => entry.code === type)?.destination ?? null;
}
